import NavBar from "../../components/NavBar/NavBar";
import Footer from "../../components/Footer/Footer";
import Button from "../../components/Button/Button";
import "./ProductPage.scss";
import portfolioBuilder from "../../assets/products/portfolio-builder.jpg";
import {
  FaUsers,
  FaUserTie,
  FaChartLine,
  FaClipboardList,
  FaFileAlt,
  FaClock
} from "react-icons/fa";

interface PortfolioBuilderPageProps {
  product: {
    id: string;
    price: number;
    length: string;
    comingSoon: boolean;
  };
}

const PortfolioBuilderPage = ({ product }: PortfolioBuilderPageProps) => {
  const features = [
    {
      icon: <FaUserTie />,
      title: "Expert Portfolio Review",
      description: "1-to-1 expert portfolio review with a top-10 ranked Ophthalmology trainee"
    },
    {
      icon: <FaUsers />,
      title: "Consultant Advice",
      description: "Advice from consultant ophthalmologists who are actively involved in recruitment"
    },
    {
      icon: <FaChartLine />,
      title: "Evidence Mapping",
      description: "Evidence mapping against scoring domains (audit, teaching, leadership, research)"
    },
    {
      icon: <FaClipboardList />,
      title: "Formatting Guidance",
      description: "Guidance on formatting, presentation, and high-impact content"
    },
    {
      icon: <FaFileAlt />,
      title: "Templates & Checklists",
      description: "Practical templates and checklists to organise your folder"
    },
    {
      icon: <FaClock />,
      title: "Full Day Sessions",
      description: "2 sessions across a full day for comprehensive portfolio development"
    }
  ];

  return (
    <>
      <NavBar />
      <div id="ProductPage" className="product-page--oq">
        <section className="oq-product-hero" aria-label="Portfolio Builder">
          <div className="site-container oq-product-hero__inner">
            <div className="oq-product-hero__copy">
              <div className="portfolio-builder-limited-badge">
                <FaUsers aria-hidden />
                <span>Limited spaces — book early to secure your place</span>
              </div>
              <p className="oq-product-hero__eyebrow">Ophthalmology ST1 portfolio</p>
              <h1 className="oq-product-hero__title">Portfolio Builder</h1>
              <p className="oq-product-hero__lead">
                For trainee and junior doctors building a portfolio to reach the Ophthalmology interview stage.
                Comprehensive portfolio review to help you gain more points and improve your score—with{" "}
                <strong>personalised support</strong> from top-ranked trainees and consultant ophthalmologists.
              </p>
              <div className="oq-product-hero__pricing">
                <span className="oq-product-hero__price">£{product.price}</span>
                <span className="oq-product-hero__length">/ {product.length}</span>
                {product.comingSoon && <span className="coming-soon-pill">coming soon</span>}
              </div>
              {!product.comingSoon && (
                <div className="oq-product-hero__signup">
                  <Button text="Buy now" linkTo={`/sign-up?product=${product.id}`} type="navy" />
                </div>
              )}
            </div>
            <div className="oq-product-hero__visual">
              <div className="oq-product-hero__frame">
                <img
                  className="oq-product-hero__image"
                  src={portfolioBuilder}
                  alt="Portfolio Builder — expert portfolio review and ST1 preparation"
                  width={640}
                  height={427}
                  loading="eager"
                  decoding="async"
                />
              </div>
            </div>
          </div>
        </section>

        <div className="product-sales-content">
          <div className="site-container">
            <section className="sales-section intro-section">
              <p className="lead-text">
                Stand out in the highly competitive Ophthalmology ST recruitment process. Your evidence folder can be
                worth up to 50 points in national selection — every detail counts.
              </p>
              <p className="solution-text">
                <strong>Get the edge before the interview stage.</strong>
              </p>
            </section>

            <section className="sales-section portfolio-builder-whats-included">
              <h2 className="portfolio-builder-section-heading">What You Get</h2>
              <p className="portfolio-builder-section-lead">
                This service is designed for trainee and junior doctors to build a portfolio to reach the Ophthalmology
                interview stage and gain more points to improve their portfolio score.
              </p>

              <div className="portfolio-builder-features-grid">
                {features.map((feature, index) => (
                  <div key={index} className="portfolio-builder-feature-card">
                    <div className="portfolio-builder-feature-icon">{feature.icon}</div>
                    <h3>{feature.title}</h3>
                    <p>{feature.description}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="sales-section pricing-section">
              <div className="pricing-card">
                <h3>Get Started Today</h3>
                <p className="portfolio-builder-pricing-limited-pill" role="status">
                  <FaUsers aria-hidden />
                  <span>Limited spots — book early</span>
                </p>
                <div className="pricing-amount">
                  <div className="portfolio-builder-price-row">
                    <span className="price-large">£{product.price}</span>
                    <span className="price-period">/ {product.length}</span>
                  </div>
                  {product.comingSoon && (
                    <div className="portfolio-builder-coming-soon-wrap">
                      <span className="coming-soon-pill">coming soon</span>
                    </div>
                  )}
                </div>
                <p className="portfolio-builder-pricing-note">2 sessions across a full day</p>
                <p className="portfolio-builder-pricing-note portfolio-builder-pricing-note--small">
                  Contact us to discuss your portfolio building needs and get started.
                </p>
                <div className="pricing-cta">
                  {!product.comingSoon && (
                    <Button text="Buy now" linkTo={`/sign-up?product=${product.id}`} type="navy" />
                  )}
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default PortfolioBuilderPage;
